import { getIP } from "src/utils/ip";
import { getEnvSettings } from "./configurationServices";
import { getFingerprint as fetchFingerprint } from "src/utils/fingerprint";

const TOKEN_STORAGE_KEY = "placebet_oauth2_token";

// --- START: Example Types ---
export type WorkstationTypeEnum = 'None' | 'Terminal' | 'Mobile' | 'Kiosk' | 'TOUCH' | 'KIOSK'

export interface EnabledProduct {
  id: string
  name: string
  printFooter: boolean
  printHeader: boolean
}

export interface PrintSettings {
  printLogoEnterprise: boolean
  printMethod: 'BROWSER' | 'THERMAL' | 'NETWORK'
  printInqueryQR: boolean
}

export interface WorkstationSettings {
  barcodeReaderType: 'HANDHELD' | 'ER1000' | 'NONE'
  betAmountAlert: number
  enabled_products: EnabledProduct[]
  print: PrintSettings
}

export interface Settings {
  logo: string
  singleAgency: boolean
  slogan: string
  subdomain: string
}

export interface OAuthToken {
  access_token: string
  active: boolean
  client_id: string
  client_secret: string
  enterprise_settings: Settings
  expires_in: number
  scope: string
  settings: WorkstationSettings
  token_type: string
  workstation_id: string
  workstation_name: string
  workstation_type: WorkstationTypeEnum
}

export interface StoredTokenData extends OAuthToken {
  timestamp: number
}
// --- END: Example Types ---

/**
 * Configuración de refresh token
 */
const TOKEN_REFRESH_BUFFER_SECONDS = 60; // Renovar 60 segundos antes de expirar

/**
 * Checks if a stored token is still valid
 */
const isTokenValid = (storedToken: StoredTokenData): boolean => {
  if (!storedToken.active) {
    return false;
  }

  const now = Date.now();
  const tokenAge = (now - storedToken.timestamp) / 1000; // Age in seconds
  const expiresIn = storedToken.expires_in;

  // Add buffer to avoid using tokens that are about to expire
  return tokenAge < (expiresIn - TOKEN_REFRESH_BUFFER_SECONDS);
};

/**
 * Checks if token needs refresh (approaching expiration but still valid)
 */
export const tokenNeedsRefresh = (storedToken: StoredTokenData | null): boolean => {
  if (!storedToken || !storedToken.active) {
    return false;
  }

  const now = Date.now();
  const tokenAge = (now - storedToken.timestamp) / 1000;
  const expiresIn = storedToken.expires_in;
  
  const needsRefresh = tokenAge >= (expiresIn - TOKEN_REFRESH_BUFFER_SECONDS) && tokenAge < expiresIn;
  
  return needsRefresh;
};

/**
 * Gets the time until token expires in milliseconds
 */
export const getTokenExpiresInMs = (storedToken: StoredTokenData | null): number => {
  if (!storedToken) return 0;
  
  const now = Date.now();
  const tokenAge = (now - storedToken.timestamp) / 1000;
  const remainingSeconds = storedToken.expires_in - tokenAge;
  
  return Math.max(0, remainingSeconds * 1000);
};

/**
 * Gets the current stored token from session storage
 */
const getStoredToken = (): StoredTokenData | null => {
  try {
    const raw = sessionStorage.getItem(TOKEN_STORAGE_KEY);
    if (!raw) return null;

    return JSON.parse(raw) as StoredTokenData;
  } catch (error) {
    console.error("Error parsing stored token:", error);
    return null;
  }
};

/**
 * Stores the token data in session storage
 */
const storeToken = (tokenData: OAuthToken): StoredTokenData => {
  const storedTokenData: StoredTokenData = {
    ...tokenData,
    timestamp: Date.now()
  };

  sessionStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(storedTokenData));
  
  // Also store legacy keys as in example if needed, but the service uses TOKEN_STORAGE_KEY
  const expiry = Date.now() + (tokenData.expires_in - 60) * 1000;
  sessionStorage.setItem('access_token', tokenData.access_token);
  sessionStorage.setItem('access_token_expiry', expiry.toString());

  return storedTokenData;
};

const getFingerprint = async () => {
  // Use existing fingerprint but return in the format expected by the logic
  const visitorId = await fetchFingerprint();
  return {
    visitorId,
    metadata: {} // Since current utils only return visitorId
  };
}

/**
 * Requests a new token from the OAuth2 endpoint
 * @param providedFingerprint - Optional pre-generated fingerprint to use instead of generating a new one
 * @param providedMetadata - Optional metadata associated with the provided fingerprint
 * @param userId - Optional user ID for identification header
 */
export async function fetchToken(
  providedFingerprint?: string,
  providedMetadata?: Record<string, unknown>,
  userId?: string
): Promise<{ access_token: string, payload: OAuthToken }> {

  // Use provided fingerprint or get from current logic
  let visitorId: string;
  let metadata: Record<string, unknown>;

  if (providedFingerprint) {
    visitorId = providedFingerprint;
    metadata = providedMetadata || {};
  } else {
    const cached = await getFingerprint();
    visitorId = cached.visitorId;
    metadata = cached.metadata;
  }

  // 1) Build URLSearchParams with the fingerprint
  const params = new URLSearchParams({
    fingerprint: '8HGOHhnwOkTDYaWVpLtg'
  })

  // 2) If there's a "registration_key" in the URL, add it
  if (typeof globalThis !== 'undefined') {
    const urlParams = new URLSearchParams(globalThis.location.search)
    const regKey = urlParams.get('registration_key')
    if (regKey) {
      params.append('registration_key', regKey)
    }
  }

  const ip = await getIP()

  // 3) Serialize and POST
  const form = params.toString()
  // Build X-Placebet-Identifier header if userId is provided
  const placebetIdentifier = userId
    ? [...globalThis.btoa(JSON.stringify({ userId }))].reverse().join('')
    : '';

  const res = await fetch(
    `${getEnvSettings().REACT_APP_PB_API_BASE_URL}/oauth2/token`,
    {
      method: 'POST',
      mode: 'cors', // ensure browser sends Origin header
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'application/json',
        'x-placebet-ip-address': ip,
        // 'x-provider-token': token || '', // "no uses x-provider-token" as requested
        'x-placebet-device-metadata': globalThis.btoa(JSON.stringify(metadata)),
      },
      body: form
    }
  )

  if (!res.ok) {
    const text = await res.text()
    console.error('Token fetch failed:', text)
    return { access_token: '', payload: {} as OAuthToken }
  }

  const payload: OAuthToken = await res.json()
  const access_token: string = payload.access_token

  if (access_token) {
    storeToken(payload);
    return { access_token, payload }
  }

  return { access_token: '', payload: {} as OAuthToken }
}

/**
 * Gets a secure token, either from storage (if valid) or by requesting a new one
 */
export const getSecureToken = async (forceRefresh = false): Promise<StoredTokenData> => {
  const storedToken = getStoredToken();

  if (!forceRefresh && storedToken && isTokenValid(storedToken)) {
    return storedToken;
  }

  const { payload } = await fetchToken();
  return storeToken(payload);
};

/**
 * Refreshes the token if it's about to expire
 */
export const refreshTokenIfNeeded = async (): Promise<StoredTokenData | null> => {
  const storedToken = getStoredToken();
  
  if (tokenNeedsRefresh(storedToken)) {
    return getSecureToken(true);
  }
  
  return null;
};

/**
 * Clears the stored token from session storage
 */
export const clearStoredToken = (): void => {
  sessionStorage.removeItem(TOKEN_STORAGE_KEY);
  sessionStorage.removeItem('access_token');
  sessionStorage.removeItem('access_token_expiry');
};

/**
 * Gets the current stored token without validation
 */
export const getCurrentStoredToken = (): StoredTokenData | null => {
  return getStoredToken();
};

/**
 * JWT Payload interface
 */
interface JWTPayload {
  sub?: string;
  ext?: {
    agency_name?: string;
    agency_id?: string;
    enterprise_name?: string;
    enterprise_id?: string;
    workstation_name?: string;
    workstation_id?: string;
    workstation_type?: string;
    device_id?: string;
    active?: boolean;
    bet_amount_alert?: number;
    print_method?: string | null;
    roles?: string[];
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

/**
 * Decodes a JWT token and returns its payload
 */
const decodeJWT = (token: string): JWTPayload | null => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replaceAll('-', '+').replaceAll('_', '/');
    const jsonPayload = decodeURIComponent(
      [...atob(base64)]
        .map((c) => '%' + ('00' + c.codePointAt(0)?.toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload) as JWTPayload;
  } catch (error) {
    console.error("Error decoding JWT:", error);
    return null;
  }
};

/**
 * Extracts the user ID (sub) from the stored token
 */
export const getUserIdFromToken = (): string | null => {
  const storedToken = getStoredToken();
  if (!storedToken?.access_token) {
    return null;
  }

  const payload = decodeJWT(storedToken.access_token);
  return payload?.sub || null;
};

/**
 * Extracts the agency name from the stored token
 */
export const getAgencyNameFromToken = (): string | null => {
  const storedToken = getStoredToken();
  if (!storedToken?.access_token) {
    return null;
  }

  const payload = decodeJWT(storedToken.access_token);
  return payload?.ext?.agency_name || null;
};

/**
 * Extracts the enterprise ID from the stored token
 */
export const getEnterpriseIdFromToken = (): string | null => {
  const storedToken = getStoredToken();
  if (!storedToken?.access_token) {
    return null;
  }

  const payload = decodeJWT(storedToken.access_token);
  return (payload?.ext?.enterprise_id as string) || null;
};

/**
 * Extracts the enterprise name from the stored token
 */
export const getEnterpriseNameFromToken = (): string | null => {
  const storedToken = getStoredToken();
  if (!storedToken?.access_token) {
    return null;
  }

  const payload = decodeJWT(storedToken.access_token);
  return payload?.ext?.enterprise_name || null;
};

/**
 * Extracts the workstation ID from the stored token
 */
export const getWorkstationIdFromToken = (): string | null => {
  const storedToken = getStoredToken();
  if (!storedToken?.access_token) {
    return null;
  }

  const payload = decodeJWT(storedToken.access_token);
  return (payload?.ext?.workstation_id as string) || null;
};

/**
 * Extracts the workstation name from the stored token
 */
export const getWorkstationNameFromToken = (): string | null => {
  const storedToken = getStoredToken();
  if (!storedToken?.access_token) {
    return null;
  }

  const payload = decodeJWT(storedToken.access_token);
  return payload?.ext?.workstation_name || null;
};

/**
 * Extracts the agency ID from the stored token
 */
export const getAgencyIdFromToken = (): string | null => {
  const storedToken = getStoredToken();
  if (!storedToken?.access_token) {
    return null;
  }

  const payload = decodeJWT(storedToken.access_token);
  return payload?.ext?.agency_id || null;
};

